/**
 * Automated calls: the installation's telephony provider and Connect's account
 * with it, the number calls come from, the hours and the retries.
 *
 * The Exotel token is typed here and never shown again — the API stores it
 * encrypted and answers only whether one is set, so a blank field keeps it.
 * Calling cannot be switched on half-configured: the API refuses, and so does
 * this form. Once saved, the page lists the four addresses Exotel's call flow
 * is given, once, when the flow is built in Exotel's App Bazaar.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, PhoneCall } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  PageHeader,
  PasswordInput,
  QueryPending,
  ReadOnly,
  Select,
  Switch,
  api,
  toast,
  useCan,
} from '@connect/web-shared';

import { EXOTEL_HOSTS, callingKeys, type CallingOverview } from './calling';

export interface CallingForm {
  enabled: boolean;
  provider: string;
  caller_id: string;
  max_attempts: string;
  retry_minutes: string;
  window_start: string;
  window_end: string;
  host: string;
  account_sid: string;
  api_key: string;
  api_token: string;
  flow_id: string;
}

function formOf(overview: CallingOverview): CallingForm {
  const p = overview.platform;
  return {
    enabled: p.enabled,
    provider: p.provider ?? overview.providers[0]?.id ?? '',
    caller_id: p.caller_id ?? '',
    max_attempts: String(p.max_attempts),
    retry_minutes: String(p.retry_minutes),
    window_start: p.window_start,
    window_end: p.window_end,
    host: p.exotel.host,
    account_sid: p.exotel.account_sid,
    api_key: p.exotel.api_key,
    api_token: '',
    flow_id: p.exotel.flow_id,
  };
}

/** Everything a call needs, so the switch can say why it cannot turn on. */
export function missing(form: CallingForm, tokenStored: boolean): string[] {
  const out: string[] = [];
  if (!form.provider) out.push('the provider');
  if (!form.caller_id.trim()) out.push('the number calls come from');
  if (form.provider === 'exotel') {
    if (!form.account_sid.trim()) out.push('the account SID');
    if (!form.api_key.trim()) out.push('the API key');
    if (!form.api_token.trim() && !tokenStored) out.push('the API token');
    if (!/^\d+$/.test(form.flow_id.trim())) out.push('the call flow ID');
  }
  return out;
}

export function CallingPage() {
  const overview = useQuery({
    queryKey: callingKeys.overview,
    queryFn: () => api.get<CallingOverview>('/calling/admin'),
  });
  return (
    <div className="space-y-4">
      <PageHeader
        title="Automated calls"
        description="The calls Connect places for clients — a callback nobody made, a reminder — with a menu the customer answers by pressing a key."
      />
      {overview.data === undefined ? (
        <QueryPending query={overview} label="Loading automated calls" />
      ) : (
        <Editor key={JSON.stringify(overview.data.platform)} overview={overview.data} />
      )}
    </div>
  );
}

function Editor({ overview }: { overview: CallingOverview }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.calling.edit');
  const [form, setForm] = useState<CallingForm>(() => formOf(overview));
  const set = (patch: Partial<CallingForm>) => setForm((f) => ({ ...f, ...patch }));
  const tokenStored = overview.platform.exotel.api_token_set;
  const gaps = missing(form, tokenStored);
  const changed = JSON.stringify(form) !== JSON.stringify(formOf(overview));

  const save = useMutation({
    mutationFn: (rotate: boolean) =>
      api.put<CallingOverview>('/calling/admin', {
        enabled: form.enabled,
        provider: form.provider || null,
        caller_id: form.caller_id.trim(),
        max_attempts: Number(form.max_attempts),
        retry_minutes: Number(form.retry_minutes),
        window_start: form.window_start,
        window_end: form.window_end,
        exotel: {
          host: form.host,
          account_sid: form.account_sid.trim(),
          api_key: form.api_key.trim(),
          api_token: form.api_token.trim(),
          flow_id: form.flow_id.trim(),
        },
        rotate_webhook_key: rotate,
      }),
    onSuccess: (data, rotate) => {
      queryClient.setQueryData(callingKeys.overview, data);
      toast.success(
        rotate ? 'New addresses issued' : 'Automated calls saved',
        rotate ? 'Give Exotel’s call flow the new addresses below.' : undefined,
      );
    },
    onError: (e) => toast.error('Not saved', e instanceof ApiError ? e.message : undefined),
  });

  const attempts = Number(form.max_attempts);
  const minutes = Number(form.retry_minutes);
  const malformed =
    !(attempts >= 1 && attempts <= 5) ||
    !(minutes >= 5 && minutes <= 1440) ||
    form.window_start >= form.window_end;

  return (
    <ReadOnly when={!canEdit}>
      <div className="space-y-4">
        <Card>
          <CardHeader
            title="Calling"
            description="Off, no client is called and no client sees calls. On, calls are made for the clients switched on under their Services."
            actions={overview.ready ? <Badge tone="success">On</Badge> : <Badge tone="neutral">Off</Badge>}
          />
          <CardBody className="space-y-4">
            <Switch
              label="Make automated calls"
              hint={
                form.enabled && gaps.length > 0
                  ? `Fill in ${gaps.join(', ')} first.`
                  : 'Clients are switched on one by one, on their Services tab.'
              }
              checked={form.enabled}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Provider"
                options={overview.providers.map((p) => ({ value: p.id, label: p.label }))}
                placeholder="Choose a provider"
                value={form.provider}
                onChange={(e) => set({ provider: e.target.value })}
              />
              <Input
                label="Number calls come from"
                hint="The ExoPhone, as Exotel shows it."
                inputMode="tel"
                value={form.caller_id}
                onChange={(e) => set({ caller_id: e.target.value })}
              />
              <Input
                label="Calls from"
                type="time"
                value={form.window_start}
                onChange={(e) => set({ window_start: e.target.value })}
              />
              <Input
                label="Calls until"
                type="time"
                hint="In the office's time zone. A call due outside these hours waits for them."
                value={form.window_end}
                error={form.window_start >= form.window_end ? 'Must end after it starts' : undefined}
                onChange={(e) => set({ window_end: e.target.value })}
              />
              <Input
                label="Tries per call"
                inputMode="numeric"
                hint="From 1 to 5, counting the first."
                value={form.max_attempts}
                onChange={(e) => set({ max_attempts: e.target.value.replace(/\D/g, '') })}
              />
              <Input
                label="Minutes between tries"
                inputMode="numeric"
                hint="When a call is busy or not answered. From 5 to 1440."
                value={form.retry_minutes}
                onChange={(e) => set({ retry_minutes: e.target.value.replace(/\D/g, '') })}
              />
            </div>
          </CardBody>
        </Card>
  
        {form.provider === 'exotel' ? (
          <Card>
            <CardHeader
              title="Exotel account"
              description="Connect's own account with Exotel. Find these under API settings in Exotel's dashboard."
            />
            <CardBody className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Cluster"
                options={EXOTEL_HOSTS}
                value={form.host}
                onChange={(e) => set({ host: e.target.value })}
              />
              <Input
                label="Account SID"
                value={form.account_sid}
                onChange={(e) => set({ account_sid: e.target.value })}
              />
              <Input
                label="API key"
                autoComplete="off"
                value={form.api_key}
                onChange={(e) => set({ api_key: e.target.value })}
              />
              <PasswordInput
                label="API token"
                autoComplete="new-password"
                placeholder={tokenStored ? 'Stored — type to replace it' : ''}
                hint={tokenStored ? 'Kept encrypted, and never shown again.' : 'Stored encrypted, and never shown again.'}
                value={form.api_token}
                onChange={(e) => set({ api_token: e.target.value })}
              />
              <Input
                label="Call flow ID"
                inputMode="numeric"
                hint="The app built in App Bazaar for Connect's calls (below)."
                value={form.flow_id}
                onChange={(e) => set({ flow_id: e.target.value.replace(/\D/g, '') })}
              />
            </CardBody>
          </Card>
        ) : null}
  
        {canEdit && changed ? (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setForm(formOf(overview))}>
              Discard
            </Button>
            <Button
              loading={save.isPending}
              disabled={malformed || (form.enabled && gaps.length > 0)}
              onClick={() => save.mutate(false)}
            >
              Save
            </Button>
          </div>
        ) : null}
  
        {form.provider === 'exotel' && overview.flow_addresses ? (
          <FlowAddresses addresses={overview.flow_addresses} canRotate={canEdit} onRotate={() => save.mutate(true)} />
        ) : null}
      </div>
    </ReadOnly>
  );
}

function FlowAddresses({
  addresses,
  canRotate,
  onRotate,
}: {
  addresses: NonNullable<CallingOverview['flow_addresses']>;
  canRotate: boolean;
  onRotate: () => void;
}) {
  const steps = [
    { step: '1. Gather', how: 'Dynamic URL. Set the language your customers hear here.', url: addresses.gather },
    { step: '2. Passthru', how: 'Make passthru synchronous.', url: addresses.passthru },
    { step: '3. Greeting', how: '“Read text like a robot”, with this address as the text.', url: addresses.greeting },
    { step: '4. Hangup', how: 'Nothing to set.', url: null },
  ];
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Copied');
    } catch {
      toast.error('Not copied', 'Select the address and copy it by hand.');
    }
  };
  return (
    <Card>
      <CardHeader
        title="Exotel call flow"
        description="Build one app in Exotel's App Bazaar with these four steps, in this order, and put its ID above. Every call Connect places runs through it."
        actions={<PhoneCall className="size-4 text-muted" aria-hidden />}
      />
      <CardBody className="space-y-3">
        {steps.map((s) => (
          <div key={s.step} className="rounded-control border border-line p-3">
            <p className="text-sm font-medium text-body">{s.step}</p>
            <p className="text-xs text-muted">{s.how}</p>
            {s.url ? (
              <div className="mt-2 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-surface-2 px-2 py-1 font-mono text-xs text-body">
                  {s.url}
                </code>
                <Button size="sm" variant="ghost" onClick={() => void copy(s.url as string)} aria-label={`Copy the ${s.step} address`}>
                  <Copy className="size-4" aria-hidden />
                </Button>
              </div>
            ) : null}
          </div>
        ))}
        <Alert tone="info" title="Call progress">
          Connect gives Exotel the status address with each call; nothing to set in the flow. These addresses hold a secret:
          share them only with Exotel.
        </Alert>
        {canRotate ? (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={onRotate}>
              Issue new addresses
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
